import React from "react"
import {
  Card,
  CardBody,
  Row,
  Col,
  ButtonGroup,
  Button,
  Spinner,
} from "reactstrap"
import { useSelector } from "react-redux"

const Miniwidgets = ({
  reports,
  loading,
  callBack,
  viewMode,
  setViewMode,
  fromDate,
  toDate,
  setFromDate,
  setToDate,
  noFilter,
}) => {
  const mode = useSelector(state => state.Layout.layoutMode)
  const isDark = String(mode).toLowerCase().includes("dark")

  const colors = {
    cardBg: isDark ? "#252F40" : "#FFFFFF",
    text: isDark ? "#FFFFFF" : "#252F40",
    muted: isDark ? "#B9C0CA" : "#6B7280",
    iconBg: isDark ? "rgba(255,255,255,0.08)" : "#F1F1F1",
    iconFg: "#2990DF",
    up: "#58DB83",
    down: "#EF4444",
    badgeBg: isDark ? "rgba(88,219,131,0.12)" : "#E6F8EE",
  }

  const handleCustomDateRange = () => {
    if (fromDate && toDate) {
      if (viewMode === "Custom") callBack()
      else setViewMode("Custom")
    }
  }

  const clearFilters = () => {
    if (viewMode !== "Month" || fromDate !== "" || toDate !== "") {
      setViewMode("Month")
      setFromDate("")
      setToDate("")
      callBack()
    }
  }

  return (
    <>
      {!noFilter && (
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 style={{ color: isDark ? colors.text : "#0d6efd" }}>
            Statistics
          </h4>
          <div className="d-flex align-items-center gap-2">
            <input
              type="date"
              value={fromDate}
              onChange={e => {
                setFromDate(e.target.value)
                setViewMode("Custom")
              }}
              className="form-control"
              placeholder="From Date"
              style={{
                background: colors.cardBg,
                color: colors.text,
                borderColor: isDark ? "#344356" : "#e5e7eb",
              }}
            />
            <input
              type="date"
              value={toDate}
              onChange={e => {
                setToDate(e.target.value)
                setViewMode("Custom")
              }}
              className="form-control"
              placeholder="To Date"
              style={{
                background: colors.cardBg,
                color: colors.text,
                borderColor: isDark ? "#344356" : "#e5e7eb",
              }}
            />
            <Button
              color="primary"
              onClick={handleCustomDateRange}
              disabled={!fromDate || !toDate}
            >
              Apply
            </Button>
            <ButtonGroup>
              <Button
                color={viewMode === "Month" ? "primary" : "outline-primary"}
                onClick={() => setViewMode("Month")}
              >
                Month
              </Button>
              <Button
                color={viewMode === "Week" ? "primary" : "outline-primary"}
                onClick={() => setViewMode("Week")}
              >
                Week
              </Button>
            </ButtonGroup>
            <Button color="danger" outline onClick={clearFilters}>
              Reset
            </Button>
          </div>
        </div>
      )}

      <Row>
        {reports.map((report, key) => {
          const isUp = report?.average?.startsWith("+")
          const trendColor = isUp ? colors.up : colors.down

          return (
            <Col xl={3} sm={6} key={key}>
              <Card
                className="mini-stat mt-4"
                style={{
                  background: colors.cardBg,
                  borderRadius: 14,
                  border: `1px solid ${isDark ? "#344356" : "#e5e7eb"}`,
                }}
              >
                <CardBody className="card-body px-3 py-4">
                  {loading ? (
                    <Spinner size="lg" color={isDark ? "light" : "primary"} />
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                      }}
                    >
                      {/* ROW 1: Title */}
                      <div>
                        <h6
                          style={{
                            margin: 0,
                            fontSize: 14,
                            letterSpacing: 0.5,
                            textTransform: "uppercase",
                            color: colors.muted,
                            fontWeight: 600,
                          }}
                        >
                          {report.title}
                        </h6>
                      </div>

                      {/* ROW 2: Total + Trend (left) | Round Icon (right) */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        {/* Left: total + trend */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <h2
                            style={{
                              margin: 0,
                              fontSize: 30,
                              color: colors.text,
                              fontWeight: 700,
                            }}
                          >
                            {report.total}
                          </h2>
                          {report.average && !noFilter && (
                            <i
                              className={`mdi mdi-trending-${isUp ? "up" : "down"}`}
                              style={{
                                fontSize: 24,
                                color: trendColor,
                                lineHeight: 1,
                              }}
                            />
                          )}
                        </div>

                        {/* Right: round icon */}
                        <div
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: "50%",
                            background: colors.iconBg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <i
                            className={`mdi mdi-${report.iconClass}`}
                            style={{ fontSize: 22, color: colors.iconFg }}
                          />
                        </div>
                      </div>

                      {/* ROW 3: Badge + subtext */}
                      {/* <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            padding: "4px 8px",
                            borderRadius: 8,
                            fontWeight: 600,
                            fontSize: 12,
                            background:
                              report.average === null
                                ? "transparent"
                                : colors.badgeBg,
                            color:
                              report.average === null
                                ? colors.muted
                                : trendColor,
                            border:
                              report.average === null
                                ? `1px dashed ${isDark ? "#344356" : "#e5e7eb"}`
                                : "none",
                          }}
                        >
                          {report.average}
                        </span>

                        <span
                          style={{
                            marginLeft: report.average ? 8 : 0,
                            color: colors.muted,
                            fontSize: 13,
                          }}
                        >
                          {report.average === null
                            ? "Sum of connected review platforms"
                            : noFilter
                              ? " "
                              : `From previous ${viewMode}`}
                        </span>
                      </div> */}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          )
        })}
      </Row>
    </>
  )
}

export default Miniwidgets
